import { Color } from '@persona-id/design-tokens';
import FocusTrap from 'focus-trap-react';
import { MouseEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import Markdown from 'shared/components/Markdown';
import { MEDIA_QUERIES } from 'shared/styles/variables';
import styled from 'styled-components';

import PopupBackdrop from 'inquiry/components/PopupBackdrop';
import UiComponentView from 'inquiry/components/UiComponentView';
import { CREATE_PERSONA_MESSAGES } from 'inquiry/lib/reusablePersona';
import { CreatePersonaStatus, setCreatePersonaStatus } from 'inquiry/redux/actions/create-persona';
import EventsClient from 'lib/clients/EventsClient';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useInquiryFeatureFlag from 'lib/hooks/useInquiryFeatureFlag';
import { useEscListener } from 'lib/hooks/useKeyListener';
import useToggle from 'lib/hooks/useToggle';
import { colorCss, sizeCss } from 'lib/inquiryStyles';
import { PageTransition } from 'lib/interfaces/ThemeSerialized';
import { ButtonCreatePersonaComponent } from 'lib/interfaces/ui-components';
import { focusTitleElement } from 'verify/lib/a11y';
import { HolderClient } from 'verify/lib/clients/HolderClient';
import SessionClientVerify from 'verify/lib/clients/SessionClientVerify';
import { flowHasCompleteAction } from 'verify/lib/inquiry';
import { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import { fontFamilyCss } from 'verify/lib/utils/font-family';
import { Inquiry } from 'verify/models/inquiry';
import { savePopupUrl } from 'verify/redux/actions/inquiry';
import { RootState } from 'verify/redux/store';
import { Z_INDICES } from 'verify/styles/variables';

interface ButtonCreatePersonaProps {
  component: ButtonCreatePersonaComponent;
  actionsApi: UiComponentActionsApi;
  inquiry: Inquiry;
  inquiryLoading: boolean;
}

const POPUP_WIDTH = 400;
const POPUP_HEIGHT = 650;
const POPUP_WINDOW_NAME = 'reusable-persona-create-popup';

const SLIDE_DURATION = 250;

const Backdrop = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${Z_INDICES.drawer};

  pointer-events: none; // page underneath should still be interactable

  @media ${MEDIA_QUERIES.isMobile} {
    position: fixed;
  }
`;

const Anim = styled.div<{ styles: ButtonCreatePersonaComponent['styles'] }>`
  position: absolute;

  bottom: ${({ styles }) => {
    return sizeCss(styles?.margin?.base?.base?.bottom);
  }};
  left: ${({ styles }) => {
    return sizeCss(styles?.margin?.base?.base?.left);
  }};
  right: ${({ styles }) => {
    return sizeCss(styles?.margin?.base?.base?.right);
  }};

  transition: ${SLIDE_DURATION}ms;

  .dialog-enter & {
    transform: translateY(
      calc(100% + ${({ styles }) => sizeCss(styles?.margin?.base?.base?.bottom) ?? '0px'})
    );
  }
  .dialog-enter-active & {
    transform: translateY(0%);
  }

  .dialog-exit & {
    transform: translateY(0%);
  }
  .dialog-exit-active & {
    transform: translateY(
      calc(100% + ${({ styles }) => sizeCss(styles?.margin?.base?.base?.bottom) ?? '0px'})
    );
  }

  z-index: ${Z_INDICES.drawer};

  pointer-events: auto;
`;

const CtaCard = styled.div<{ styles: ButtonCreatePersonaComponent['styles'] }>`
  z-index: ${Z_INDICES.drawer};

  padding-top: ${({ styles }) => sizeCss(styles?.padding?.base?.base?.top)};
  padding-bottom: ${({ styles }) => sizeCss(styles?.padding?.base?.base?.bottom)};
  padding-left: ${({ styles }) => sizeCss(styles?.padding?.base?.base?.left)};
  padding-right: ${({ styles }) => sizeCss(styles?.padding?.base?.base?.right)};

  background-color: ${({ styles }) => colorCss(styles?.['background-color']?.base?.base)};
  border-color: ${({ styles }) => colorCss(styles?.['border-color']?.base?.base)};
  border-width: ${({ styles }) => sizeCss(styles?.['border-width']?.base?.base?.top)};
  border-radius: ${({ styles }) => sizeCss(styles?.['border-radius']?.base?.base)};

  /* TODO strict-migration] The following line is not typed properly; please fix! */
  box-shadow: ${({ styles }) => sizeCss(styles?.['shadow-offset']?.base?.base?.x)}
    /* TODO strict-migration] The following line is not typed properly; please fix! */
    ${({ styles }) => sizeCss(styles?.['shadow-offset']?.base?.base?.y)}
    ${({ styles }) => sizeCss(styles?.['shadow-radius']?.base?.base)} 0
    ${({ styles }) => colorCss(styles?.['shadow-color']?.base?.base)};
`;

const PopupOverlayText = styled.div`
  font-family: ${({ theme }) => fontFamilyCss(theme.fontFamily)};
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${Color.PrimaryWhite};
  width: 75%;
  text-align: center;

  a {
    color: ${Color.PrimaryWhite};
    font-weight: bold;
    outline-color: ${Color.PrimaryWhite};
  }
`;

const ButtonCreatePersona = ({
  component,
  actionsApi,
  inquiry,
  inquiryLoading,
}: ButtonCreatePersonaProps) => {
  // safe access state.createPersona since it'll be undefined in FLE purposes
  const createPersonaStatus = useSelector((state: RootState) => state.createPersona?.status);
  const dispatch = useDispatch();
  const ctaCardComponents =
    component.attributes['pages']['cta-card']['ui-step']['config']['components'];
  const ctaCardComponentMapping =
    component.attributes['pages']['cta-card']['component-name-mapping'];

  // default to open state if cta has already been shown
  const [isOpen, { enable: open, disable: close }] = useToggle(
    createPersonaStatus === CreatePersonaStatus.CtaShown,
  );
  useEscListener(close);

  // handle embedded flow popup
  const [t] = useTranslation();

  const externalWindowRef = useRef<Window | 'blocked' | null>(null);

  const popupUrl = useSelector<RootState, string | null>((state) => state.inquiry.popupUrl);
  const setPopupUrl = useCallback(
    (popupUrl: string | null) => dispatch(savePopupUrl(popupUrl)),
    [dispatch],
  );

  const autoCompleteOnDismiss =
    component.attributes['auto-complete-on-dismiss'] && flowHasCompleteAction(inquiry);
  const completeHasBeenCalled = useRef<boolean>(false);
  const onCompleteIfConfigured = useCallback(() => {
    if (!completeHasBeenCalled.current) {
      completeHasBeenCalled.current = true;
      if (autoCompleteOnDismiss) {
        // skip going back to original done screen to decrease dropoff rate if configured to do so
        actionsApi.onComplete();
      }
    }
  }, [actionsApi.onComplete, autoCompleteOnDismiss]);

  const closeBackdrop = useCallback(() => {
    setPopupUrl(null);
    onCompleteIfConfigured();
  }, [setPopupUrl, onCompleteIfConfigured]);

  const {
    error,
    loading: oneTimeCodeLoading,
    callback: openReusablePersonaPopup,
  } = useApiCallback(async () => {
    if (
      popupUrl &&
      externalWindowRef.current &&
      externalWindowRef.current !== 'blocked' &&
      !externalWindowRef.current.closed
    ) {
      externalWindowRef.current.focus();
      return;
    }

    // if persona has already been created, should noop
    // we shouldn't run into this in practice since the button should be hidden
    // but still put this check in, in case of race conditions in hiding the overlay
    if (createPersonaStatus === CreatePersonaStatus.PersonaCreated) {
      return;
    }

    const inquiryResponse = await SessionClientVerify.postResourceAction<Inquiry>(
      {
        id: inquiry.id,
        type: inquiry.type,
      },
      'generate-reusable-persona-create-popup-one-time-link-code',
    );

    if (inquiryResponse?.meta?.['one-time-link-code']) {
      const url = new URL(component.attributes.url);
      url.searchParams.append('code', inquiryResponse.meta['one-time-link-code']);
      url.searchParams.append('component', component.name);
      // make sure the subdomains match to prevent CORS from rejecting messages
      // generated URL will have customer subdomain but the current location
      // could just be the inquiry.* subdomain
      url.host = window.location.host;

      // triggers popup window to be shown
      setPopupUrl(url.toString());
    }
  }, [popupUrl, createPersonaStatus]);

  useEffect(() => {
    if (popupUrl) {
      const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
      const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2.5;
      externalWindowRef.current = window.open(
        popupUrl,
        POPUP_WINDOW_NAME,
        `popup,width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`,
      );

      // if ref is null, then popup window may have been blocked
      externalWindowRef.current ||= 'blocked';

      // if window is refocused, check if popup has been dismissed, clean up window ref and url
      const handleRefocus = () => {
        if (
          externalWindowRef.current === 'blocked' ||
          !externalWindowRef.current ||
          externalWindowRef.current.closed
        ) {
          externalWindowRef.current = null;
          closeBackdrop();
        }
      };

      window.addEventListener('focus', handleRefocus);

      // update create persona state based on what happens in the popup
      const handlePopupMessage = (e: MessageEvent) => {
        if (
          e.origin === new URL(popupUrl).origin &&
          (e.source === externalWindowRef.current ||
            (externalWindowRef.current === 'blocked' &&
              (e.source as WindowProxy).name === POPUP_WINDOW_NAME))
        ) {
          if (e.data === CREATE_PERSONA_MESSAGES.CREATED) {
            dispatch(setCreatePersonaStatus(CreatePersonaStatus.PersonaCreated));
            close();
            onCompleteIfConfigured();
          } else if (e.data === CREATE_PERSONA_MESSAGES.DISMISSED) {
            dispatch(setCreatePersonaStatus(CreatePersonaStatus.CtaDismissed));
            // dismiss persona action closes the window on a delay to ensure the message is sent
            // we can trigger the close earlier here
            if (externalWindowRef.current !== 'blocked') {
              externalWindowRef.current?.close();
            }
            close();
            onCompleteIfConfigured();
          }
        }
      };

      window.addEventListener('message', handlePopupMessage);

      // if the main window is closed, close the popup window
      const handleWindowClose = () => {
        if (externalWindowRef.current !== 'blocked') {
          externalWindowRef.current?.close();
        }
      };

      window.addEventListener('close', handleWindowClose);

      return () => {
        window.removeEventListener('focus', handleRefocus);
        window.removeEventListener('message', handlePopupMessage);
        window.removeEventListener('close', handleWindowClose);
      };
    } else {
      if (externalWindowRef.current !== 'blocked') {
        externalWindowRef.current?.close();
      }
      externalWindowRef.current = null;
    }
  }, [popupUrl]);

  useEffect(() => {
    return () => {
      // if we navigate away from the page and this component becomes unmounted, close the popup window
      setPopupUrl(null);
      if (externalWindowRef.current !== 'blocked') {
        externalWindowRef.current?.close();
      }
      externalWindowRef.current = null;
    };
  }, []);

  useApiErrorHandler({
    error,
    onError: () => actionsApi.showMessage(t('errors.network')),
    onNetworkError: () => actionsApi.showMessage(t('errors.network')),
  });

  const handleEmbedded = useInquiryFeatureFlag('inquiry:enable-reusable-personas-embedded-web');

  const onSubmit = useCallback(
    async (componentName: string) => {
      if (componentName === ctaCardComponentMapping['start-button']) {
        if (HolderClient.isNonIframeHostedIntegration) {
          EventsClient.logAmplitude('reusable-persona.create.cta.started-hosted');
          actionsApi.setComponent(component);

          actionsApi.route('/inquiry/create-persona/passkey-registration', {
            transition: PageTransition.SlideVertical,
          });
        } else if (HolderClient.isEmbeddedOrIframeIntegration && handleEmbedded) {
          EventsClient.logAmplitude('reusable-persona.create.cta.started-embedded');
          await openReusablePersonaPopup();
        }
      } else if (componentName === ctaCardComponentMapping['dismiss-button']) {
        EventsClient.logAmplitude('reusable-persona.create.cta.dismissed');
        onCompleteIfConfigured();

        dispatch(setCreatePersonaStatus(CreatePersonaStatus.CtaDismissed));
        close();
      }
    },
    [
      component,
      ctaCardComponentMapping,
      actionsApi,
      close,
      dispatch,
      openReusablePersonaPopup,
      handleEmbedded,
      onCompleteIfConfigured,
    ],
  );

  const embeddedActionsApi = useMemo(
    () => ({
      ...actionsApi,
      onSubmit,
    }),
    [actionsApi, onSubmit],
  );

  useEffect(() => {
    if (createPersonaStatus === CreatePersonaStatus.CtaNotShown) {
      const timeout = setTimeout(() => {
        open();
      }, 1000);

      return () => clearTimeout(timeout);
    }
  }, [open, createPersonaStatus]);

  // Because the dialog is animated, we also track animation state to determine if the UI is ready for interaction.
  const [visible, { enable: setVisible, disable: setNotVisible }] = useToggle();

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const dialogInnerRef = useRef<HTMLDivElement>(undefined);

  const handleOpenAnimationEnd = useCallback(() => {
    setVisible();
    dispatch(setCreatePersonaStatus(CreatePersonaStatus.CtaShown));
    focusTitleElement(dialogInnerRef.current);

    EventsClient.logAmplitude('reusable-persona.create.cta.shown');
  }, []);

  const handleCloseAnimationEnd = useCallback(() => {
    // Return focus to current page heading
    setTimeout(focusTitleElement, 0);
  }, []);

  const shouldRender = useMemo(() => {
    // if inquiry was verified or has created persona don't show the component
    if (inquiry.attributes['reusable-persona-status']) {
      return false;
    }

    if (HolderClient.isEmbeddedOrIframeIntegration) {
      return handleEmbedded;
    }

    return HolderClient.isNonIframeHostedIntegration;
  }, [handleEmbedded, inquiry.attributes]);

  useEffect(() => {
    if (shouldRender) {
      EventsClient.logAmplitude('reusable-persona.create.btn-rendered');
    }
  }, []);

  const popupOverlayTextMarkdownOptions = {
    overrides: {
      a: {
        component: styled.a``,
        props: {
          target: POPUP_WINDOW_NAME,
          onClick: (event: MouseEvent<HTMLElement>) => {
            // if popupURL was blocked and the popupURL is present, don't
            // prevent the default click event since the link should now be popupURL
            if (externalWindowRef.current !== 'blocked' || !popupUrl) {
              event.preventDefault();
              openReusablePersonaPopup();
            }
          },
        },
      },
    },
  };

  if (!shouldRender) {
    return null;
  }

  // card logic copied from front-end/inquiry/components/Dialog/Dialog.tsx
  return (
    <>
      <PopupBackdrop
        // hide backdrop as soon as persona is created since it has a button to return to the
        // dismissable reusable persona flow
        visible={!!popupUrl && createPersonaStatus !== CreatePersonaStatus.PersonaCreated}
        onClose={closeBackdrop}
      >
        <PopupOverlayText id='create_persona__popup_overlay_text'>
          <Markdown markdownOptions={popupOverlayTextMarkdownOptions}>
            {
              /*
                set popupURL as the link URL so that if the popup is blocked,
                it is just a regular link on the overlay and doesn't use window.open()
                */
              component.attributes['popup-overlay-text'].replace('(#)', `(${popupUrl})`)
            }
          </Markdown>
        </PopupOverlayText>
      </PopupBackdrop>
      <CSSTransition
        in={isOpen}
        timeout={{
          // don't animate entrance if cta has already been shown
          enter: createPersonaStatus === CreatePersonaStatus.CtaShown ? 0 : SLIDE_DURATION,
          exit: SLIDE_DURATION,
        }}
        mountOnEnter={true}
        unmountOnExit={true}
        onEntered={handleOpenAnimationEnd}
        onExit={setNotVisible}
        onExited={handleCloseAnimationEnd}
        classNames='dialog'
      >
        <FocusTrap
          // Use active instead of disabled because this is expected to be toggled.
          active={visible}
          focusTrapOptions={{
            // Block ALL outside interaction.
            escapeDeactivates: true,
            clickOutsideDeactivates: true,
            allowOutsideClick: true,
            // We coordinate focus outside with refs, which is easier when animations are involved.
            initialFocus: false,
            setReturnFocus: false,
            // Needed to stop FocusTrap from erroring before content has rendered.
            fallbackFocus: document.body,
          }}
        >
          <Backdrop>
            <Anim styles={component.styles}>
              <CtaCard styles={component.styles} ref={dialogInnerRef}>
                {ctaCardComponents.map((ctaCardComponent) => (
                  <UiComponentView
                    key={ctaCardComponent.name}
                    component={ctaCardComponent}
                    inquiry={inquiry}
                    inquiryLoading={inquiryLoading || oneTimeCodeLoading}
                    actionsApi={embeddedActionsApi}
                  />
                ))}
              </CtaCard>
            </Anim>
          </Backdrop>
        </FocusTrap>
      </CSSTransition>
    </>
  );
};

export default ButtonCreatePersona;
